import { Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { MsmeScreen } from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { MsmeDevSimulator } from './MsmeDevSimulator';
import { isValidUdyam } from './msme.utils';

// Fallback binding for legacy sessions whose snapshot carries no bindings.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/udyamverification',
  operationId: 'Post_Msme_Udyam',
  summary: 'MSME Verification (Udyam)',
  priority: 1,
};

/**
 * Runs one MSME Verification (Udyam) step in a live session. The applicant enters
 * their Udyam Registration Number; the backend verifier injects the consent trio +
 * the Udyam flags and calls the Udyam-without-OTP lookup (the only method Studio
 * offers). Same fallback-loop shape as the GST / Ration Card runners.
 */
export function MsmeStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [udyamNumber, setUdyamNumber] = useState('');
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const active = bindings[activeIdx] ?? bindings[0] ?? DEFAULT_BINDING;

  const udyamValid = isValidUdyam(udyamNumber);
  const ready = udyamValid;

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  function buildInput(): Record<string, unknown> {
    // Applicant data only — the backend NormalizeInput injects the consent trio and
    // the Udyam flags (isPDFRequired / getEnterpriseDetails).
    return {
      udyamRegistrationNo: udyamNumber.trim().toUpperCase(),
    };
  }

  function handleSubmit() {
    if (!ready) return;
    submitInput(buildInput());
  }

  // DEV: one-click scenario (backend MsmeMockResponses returns the canned result)
  // through the SAME flow so rules/fallback run.
  function runTestCase(testCase: string) {
    submitInput({ udyamRegistrationNo: '', test_case: testCase });
  }

  function submitInput(input: Record<string, unknown>) {
    mutate(
      { stepId: step.id, input, catalogueEntryId: active.catalogueEntryId || undefined },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          if (!updated) return;
          const status = updated.status;
          if (status === 'Passed' || status === 'Review' || status === 'Failed') {
            return;
          }
          // status Pending → technical provider failure → visible fallback.
          const nextBinding = bindings[activeIdx + 1];
          if (nextBinding) {
            setFallbackNote(tryAnotherMethodNote());
            setActiveIdx(activeIdx + 1);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      {import.meta.env.DEV && <MsmeDevSimulator onRun={runTestCase} isPending={isPending} />}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      <MsmeScreen
        method={active.summary}
        values={{ UdyamRegistrationNumber: udyamNumber }}
        onChange={(patch) => {
          if (patch.UdyamRegistrationNumber !== undefined)
            setUdyamNumber(patch.UdyamRegistrationNumber);
        }}
        errors={{
          UdyamRegistrationNumber:
            udyamNumber.length > 0 && !udyamValid
              ? 'Enter a valid Udyam number (e.g. UDYAM-MP-23-0007772).'
              : undefined,
        }}
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || !ready}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        MSME verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'Your Udyam registration could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
